import Logger from '../lib/logger';
import {
  collectFilterOptions,
  deriveProductFilterMeta,
  parseFilterTags,
  populateFilterSelect,
} from '../lib/product-filters';

class ThemeProductGrid extends HTMLElement {
  #NAME = 'ThemeProductGrid'
  #abortController
  #filterRoot
  #filterItems = []
  #filterStatuses = []
  #filterEmpty
  #filterReset
  #seriesSelect
  #statusSelect
  #featuresSelect
  #filterForm

  constructor() {
    super();
    Logger.info(`${this.#NAME} > constructor`);
    this.#filterRoot = this.querySelector('[data-filter-root]');
    this.#filterStatuses = Array.from(this.querySelectorAll('[data-filter-status]'));
    this.#filterEmpty = this.querySelector('[data-product-filters-empty]');
    this.#filterReset = this.querySelector('[data-filter-reset]');
    this.#filterForm = this.querySelector('[data-filter-form]');
    this.#seriesSelect = this.querySelector('[data-filter-control="series"]');
    this.#statusSelect = this.querySelector('[data-filter-control="status"]');
    this.#featuresSelect = this.querySelector('[data-filter-control="features"]');
  }

  connectedCallback() {
    Logger.info(`${this.#NAME} > connectedCallback`);

    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    this.#initFilterToggle(signal);
    this.#initFilterForm(signal);
    this.#initProductFilters(signal);
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    this.#abortController = null;
  }

  #initFilterToggle(signal) {
    const toggle = this.querySelector('[data-filter-toggle]');
    const panel = this.querySelector('[data-filter-panel]');

    if (!toggle || !panel) {
      return;
    }

    toggle.addEventListener('click', () => {
      const isOpen = panel.dataset.open === 'true';
      panel.dataset.open = isOpen ? 'false' : 'true';
      toggle.setAttribute('aria-expanded', isOpen ? 'false' : 'true');
    }, { signal });
  }

  #initProductFilters(signal) {
    if (!this.#filterRoot) {
      return;
    }

    // Shopify collection filters are rendered server-side; only custom PLP lists
    // need client-side option derivation and visibility toggling.
    if (!this.#seriesSelect && !this.#statusSelect && !this.#featuresSelect) {
      this.#syncFilterSelectWidths();
      return;
    }

    this.#filterItems = Array.from(this.querySelectorAll('[data-product-filter-item]')).map((item) => {
      const meta = deriveProductFilterMeta({
        collections: parseFilterTags(item.dataset.collections),
        statuses: parseFilterTags(item.dataset.statuses),
        features: parseFilterTags(item.dataset.features),
      });

      item.dataset.filterSeries = meta.series.map((option) => option.value).join('||');
      item.dataset.filterStatuses = meta.statuses.map((option) => option.value).join('||');
      item.dataset.filterFeatures = meta.features.map((option) => option.value).join('||');

      return {
        element: item,
        series: meta.series,
        statuses: meta.statuses,
        features: meta.features,
      };
    });

    const options = collectFilterOptions(this.#filterItems);
    const seriesHasChoices = options.series.length > 1;
    const statusHasChoices = options.statuses.length > 1;
    const featuresHasChoices = options.features.length > 1;
    const hasAnyMeaningfulFilters = seriesHasChoices || statusHasChoices || featuresHasChoices;

    if (!hasAnyMeaningfulFilters) {
      this.#filterRoot.hidden = true;
      return;
    }

    if (this.#seriesSelect) {
      this.#seriesSelect.closest('.theme-product-filters__field')?.toggleAttribute('hidden', !seriesHasChoices);
      populateFilterSelect(this.#seriesSelect, 'All', options.series);
      this.#seriesSelect.addEventListener('change', () => {
        this.#syncFilterSelectWidths();
        this.#applyProductFilters();
      }, { signal });
    }

    if (this.#statusSelect) {
      this.#statusSelect.closest('.theme-product-filters__field')?.toggleAttribute('hidden', !statusHasChoices);
      populateFilterSelect(this.#statusSelect, 'All', options.statuses);
      this.#statusSelect.addEventListener('change', () => {
        this.#syncFilterSelectWidths();
        this.#applyProductFilters();
      }, { signal });
    }

    if (this.#featuresSelect) {
      this.#featuresSelect.closest('.theme-product-filters__field')?.toggleAttribute('hidden', !featuresHasChoices);
      populateFilterSelect(this.#featuresSelect, 'All', options.features);
      this.#featuresSelect.addEventListener('change', () => {
        this.#syncFilterSelectWidths();
        this.#applyProductFilters();
      }, { signal });
    }

    this.#filterReset?.addEventListener('click', () => {
      if (this.#seriesSelect) {
        this.#seriesSelect.value = '';
      }

      if (this.#statusSelect) {
        this.#statusSelect.value = '';
      }

      if (this.#featuresSelect) {
        this.#featuresSelect.value = '';
      }

      this.#syncFilterSelectWidths();
      this.#applyProductFilters();
    }, { signal });

    this.#syncFilterSelectWidths();
    this.#applyProductFilters();
  }

  #initFilterForm(signal) {
    if (!this.#filterForm) {
      return;
    }

    this.#syncFilterSelectWidths();

    const statuses = this.#filterStatuses;
    const defaultStatusText = statuses[0]?.textContent?.trim() || '';

    if (this.#filterForm.hasAttribute('data-loading-status')) {
      this.#filterForm.addEventListener('submit', () => {
        this.#filterForm.dataset.loading = 'true';
        this.#filterForm.setAttribute('aria-busy', 'true');
        statuses.forEach((status) => {
          status.textContent = 'Updating...';
        });
      }, { signal });
    }

    this.#filterForm.querySelectorAll('select').forEach((select) => {
      if (select.dataset.filterControl) {
        return;
      }

      select.addEventListener('change', () => {
        this.#syncFilterSelectWidths();

        if (this.#filterForm.hasAttribute('data-loading-status')) {
          statuses.forEach((status) => {
            status.textContent = defaultStatusText;
          });
          this.#filterForm.requestSubmit();
        }
      }, { signal });
    });
  }

  #applyProductFilters() {
    if (!this.#filterRoot || !this.#filterItems.length) {
      return;
    }

    const series = this.#seriesSelect?.value || '';
    const status = this.#statusSelect?.value || '';
    const feature = this.#featuresSelect?.value || '';
    let visibleCount = 0;

    this.#filterItems.forEach((entry) => {
      const item = entry.element;
      const itemWrap = item.closest('.theme-compare-models__container__grid__item') || item;
      const itemSeries = parseFilterTags(item.dataset.filterSeries);
      const itemStatuses = parseFilterTags(item.dataset.filterStatuses);
      const itemFeatures = parseFilterTags(item.dataset.filterFeatures);
      const matchesSeries = !series || itemSeries.includes(series);
      const matchesStatus = !status || itemStatuses.includes(status);
      const matchesFeature = !feature || itemFeatures.includes(feature);
      const isMatch = matchesSeries && matchesStatus && matchesFeature;

      itemWrap.hidden = !isMatch;
      itemWrap.style.display = isMatch ? '' : 'none';

      if (isMatch) {
        visibleCount += 1;
      }
    });

    const hasFilters = Boolean(series || status || feature);

    if (this.#filterReset) {
      this.#filterReset.hidden = !hasFilters;
    }

    if (this.#filterEmpty) {
      this.#filterEmpty.hidden = visibleCount > 0;
    }

    this.#filterStatuses.forEach((status) => {
      status.textContent = `Showing ${visibleCount} product${visibleCount === 1 ? '' : 's'}`;
    });
  }

  #syncFilterSelectWidths() {
    if (!this.#filterRoot) {
      return;
    }

    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');

    if (!context) {
      return;
    }

    this.#filterRoot.querySelectorAll('select').forEach((select) => {
      const selectedOption = select.options[select.selectedIndex];
      const label = selectedOption?.textContent?.trim() || '';
      const styles = window.getComputedStyle(select);
      context.font = `${styles.fontWeight} ${styles.fontSize} ${styles.fontFamily}`;
      const textWidth = context.measureText(label).width;

      select.style.width = `${Math.ceil(textWidth + 18)}px`;
    });
  }
}

customElements.define('theme-product-grid', ThemeProductGrid);
